import React, { useEffect, useState } from 'react';
import {
  Box, Button, Grid, TextField, Alert, Paper, Typography, Divider,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SaveIcon from '@mui/icons-material/Save';
import { superAdminAPI } from '../../services/api';
import { PLATFORM_BRAND_UPDATED_EVENT } from '../../components/ui/TenantBrandLogo';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { ImageUploadField } from '../../components/ui/ImageUploadField';
import { apiErrorMessage } from '../../components/superadmin/SuperAdminDataTable';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';
import { brandColors, RADIUS } from '../../theme/appTheme';

const defaultProfile = {
  companyName: 'Tripiigo',
  logo: '',
  gstNumber: '',
  panNumber: '',
  taxLabel: 'GST',
  defaultTaxRate: 18,
  address: '',
  email: 'billing@blueflute.com',
  phone: '',
  website: 'https://blueflute.com',
  signature: '',
  authorizedSignatory: '',
  bankName: '',
  bankAccountName: '',
  bankAccount: '',
  bankIfsc: '',
  bankAddress: '',
  paymentTerms: 'Net 15',
  hsnSac: '998314',
  defaultItemDescription: 'Tripiigo TMS Subscription',
  invoiceFooter: 'Thank you for your business.',
};

export const PlatformBillingProfilePage: React.FC = () => {
  const [form, setForm] = useState(defaultProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    superAdminAPI.getBillingProfile()
      .then((r) => setForm({ ...defaultProfile, ...r.data }))
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load billing profile')))
      .finally(() => setLoading(false));
  }, []);

  const uploadAsset = async (file: File, type: 'logo' | 'signature') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('type', type);
    const res = await superAdminAPI.uploadBillingAsset(fd);
    return res.data.url as string;
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await superAdminAPI.updateBillingProfile(form);
      window.dispatchEvent(new Event(PLATFORM_BRAND_UPDATED_EVENT));
      setMessage('Platform billing profile saved. Logo appears in the sidebar, invoices & receipts.');
    } catch (err) {
      setError(apiErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><Typography>Loading billing profile...</Typography></Box>;

  return (
    <Box>
      <PageHeader
        title="Platform Billing Profile"
        subtitle={getPageSubtitle('platform_billing_profile')}
        icon={<ReceiptLongIcon />}

        action={
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving} sx={catchyPrimaryButtonSx}>
            {saving ? 'Saving…' : 'Save Profile'}
          </Button>
        }
      />
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}` }}>
            <Typography fontWeight={700} mb={2}>Company & Tax Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Company Name" value={form.companyName}
                  onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Address" multiline rows={2} value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Billing Email" value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone" value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="GST Number" value={form.gstNumber}
                  onChange={(e) => setForm((f) => ({ ...f, gstNumber: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="PAN Number" value={form.panNumber}
                  onChange={(e) => setForm((f) => ({ ...f, panNumber: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Tax Label" value={form.taxLabel}
                  onChange={(e) => setForm((f) => ({ ...f, taxLabel: e.target.value }))} sx={catchyTextFieldSx} helperText="e.g. GST, VAT, Tax" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="number" label="Default Tax Rate (%)" value={form.defaultTaxRate}
                  onChange={(e) => setForm((f) => ({ ...f, defaultTaxRate: Number(e.target.value) }))} sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Website" value={form.website}
                  onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />
            <Typography fontWeight={700} mb={2}>Invoice Defaults</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Payment Terms" value={form.paymentTerms}
                  onChange={(e) => setForm((f) => ({ ...f, paymentTerms: e.target.value }))} sx={catchyTextFieldSx}
                  helperText="e.g. Net 15" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="HSN / SAC" value={form.hsnSac}
                  onChange={(e) => setForm((f) => ({ ...f, hsnSac: e.target.value }))} sx={catchyTextFieldSx}
                  helperText="e.g. 998314 for IT services" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Default Line Item Description" value={form.defaultItemDescription}
                  onChange={(e) => setForm((f) => ({ ...f, defaultItemDescription: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
            </Grid>

            <Typography fontWeight={700} mb={2}>Bank Details (shown on invoice)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Bank Name" value={form.bankName}
                  onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Account Holder Name" value={form.bankAccountName}
                  onChange={(e) => setForm((f) => ({ ...f, bankAccountName: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Account Number" value={form.bankAccount}
                  onChange={(e) => setForm((f) => ({ ...f, bankAccount: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="IFSC / SWIFT" value={form.bankIfsc}
                  onChange={(e) => setForm((f) => ({ ...f, bankIfsc: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Bank Address" value={form.bankAddress}
                  onChange={(e) => setForm((f) => ({ ...f, bankAddress: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Invoice Footer Note" multiline rows={2} value={form.invoiceFooter}
                  onChange={(e) => setForm((f) => ({ ...f, invoiceFooter: e.target.value }))} sx={catchyTextFieldSx} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}`, mb: 2 }}>
            <ImageUploadField
              label="Company Logo"
              value={form.logo}
              helperText="Shown full-width in the super admin sidebar, plus on platform invoices & receipts. Use a PNG with transparent background for best results."
              onUpload={(file) => uploadAsset(file, 'logo')}
              onChange={(url) => setForm((f) => ({ ...f, logo: url }))}
            />
          </Paper>
          <Paper sx={{ p: 3, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}` }}>
            <ImageUploadField
              label="Authorized Signature"
              value={form.signature}
              helperText="PNG/JPG signature image for invoice footer"
              onUpload={(file) => uploadAsset(file, 'signature')}
              onChange={(url) => setForm((f) => ({ ...f, signature: url }))}
            />
            <TextField fullWidth label="Signatory Name" sx={{ mt: 2, ...catchyTextFieldSx }}
              value={form.authorizedSignatory}
              onChange={(e) => setForm((f) => ({ ...f, authorizedSignatory: e.target.value }))} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
